import {
    useState, useEffect, useCallback, useRef,
    useMemo,
} from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import {
    Badge, ListGroup, ListGroupItem, ListGroupItemHeading,
    UncontrolledTooltip,
} from 'reactstrap';
import cls from 'classnames';
import {
    orderBy, groupBy, mapValues, forEach,
} from 'lodash-es';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { pluralize } from '@s20/common/util/l10n';
import { convertTimezone, resolveJobTimeZone } from '@s20/common/util/date';
import { httpClient } from '@s20/services/httpClient';
import {
    FlexBetweenRow, IconButton, SmallPrimaryButton, toasty,
} from '../../common/forms/FormElements';
import { ApiRoutes } from '../../Common';
import { ResourcePanel } from '../ResourcePanel';
import { ModalForm } from '../../common/forms/ModalForm';
import { DEFAULT_DATETIME_FORMAT } from '../../common/util';
import { Alert } from '../../common/Modal';
import { createJobCopyDto, NAVIGATION_ACTIONS, updateResources } from './common';
import { JobSchedulerToolbar } from './JobSchedulerToolbar';
import { ColoredDateCellWrapper } from './ColoredDateCellWrapper';
import { CustomEvent } from './CustomEvent';
import { CustomShowMore } from './CustomShowMore';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import '../../assignmentCalendar/AssignmentCalendar.scss';

const localizer = momentLocalizer(moment);
const DragAndDropCalendar = withDragAndDrop(Calendar);

const CALENDAR_VIEWS = ['month', 'agenda'];
const API_DATE_FORMAT = 'YYYYMMDD';

const defaultProject = {};

const getJobTime = (job) => {
    if (!job) return '';
    return moment(job.jobDate).format('h:mm a');
};

// Main JobScheduler component
export const JobScheduler = ({
    onDateSelect,
    project = defaultProject,
    onJobSelect,
    setJobsSaving,
    isSaving,
    getJobById,
    refreshProject,
    dispatchLocations,
}) => {
    const [calendarEvents, setCalendarEvents] = useState([]);
    const [currentView, setCurrentView] = useState('month');
    const [currentDate, setCurrentDate] = useState(() => new Date());
    const [showSpanModal, setShowSpanModal] = useState(false);
    const [spanDates, setSpanDates] = useState([]);
    const [spanJob, setSpanJob] = useState(null);
    const [isApplying, setIsApplying] = useState(false);
    const [calendarKey, setCalendarKey] = useState(0);
    const [focusedEvent, setFocusedEvent] = useState(null);

    const visibleCalendarEvents = useMemo(() => calendarEvents.filter((event) => {
        if (!focusedEvent?.sessionId) {
            return true;
        }

        return focusedEvent.sessionId === event.sessionId;
    }), [calendarEvents, focusedEvent]);

    const previousEventsRef = useRef(calendarEvents);

    // Apply styles to calendar events
    const getEventStyles = useCallback(
        (event) => {
            if (currentView === 'month') {
                const isFocused = focusedEvent?.sessionId === event.sessionId;

                // When the run has cancelled days, CustomEvent paints a per-day
                // red/blue gradient over the interior, so keep the wrapper border
                // neutral (blue) instead of a solid-red 'cancelled' frame.
                const hasCancelledDays = event.cancelledDates?.length > 0;

                return {
                    className: cls(
                        !hasCancelledDays && event.isCancelled ? 'cancelled' : 'selected',
                        isFocused && 'pulse',
                    ),
                    style: { minHeight: '100%', overflow: 'visible' },
                };
            }
            return {};
        },
        [currentView, focusedEvent],
    );

    // Used to force re-render so the events are collapsed properly
    useEffect(() => {
        setCalendarKey((prev) => prev + 1);
    }, [visibleCalendarEvents]);

    // Populate calendar events from project jobs with grouping
    useEffect(() => {
        // For the purposes of grouping, give all jobs a grouping ID
        // This should catch child jobs, parent jobs, and orphan jobs (e.g. jobs where the parent has been deleted)
        const jobs = project.jobs.map((j) => ({ ...j, groupingId: j.parentJobId || j.id }));

        // Group by the grouping ID
        // Then, order by the job date
        const groupedJobs = mapValues(
            groupBy(
                jobs,
                (job) => job.groupingId,
            ),
            (jobGroup) => orderBy(jobGroup, 'jobDate'),
        );

        // Break each root group into contiguous date ranges
        const events = [];
        let eventCounter = 0;

        const createCalendarEvent = (jobRun, rootJob) => {
            if (!jobRun?.length) return;

            eventCounter += 1;

            const startDate = new Date(jobRun[0].jobDate);
            const endDate = new Date(jobRun[jobRun.length - 1].jobDate);
            endDate.setHours(23, 59, 59);

            // Use contract number with index for new jobs in new projects
            const isNewJob = typeof rootJob.id === 'string';
            const isNewProject = !project.id;

            const jobRunSource = jobRun.find((job) => job.id === rootJob.id) || jobRun[0];
            const isDraggable = !jobRun.some((job) => job.hasAssignments);

            const eventTitle = isNewJob && isNewProject
                ? `${project.contract?.number}-${eventCounter}`
                : jobRunSource.jobNumber;

            events.push({
                isDraggable,
                sessionId: `${rootJob.id}-${eventCounter}`,
                jobData: jobRun,
                title: eventTitle,
                start: startDate,
                end: endDate,
                isGroup: jobRun.length > 1,
                isCancelled: jobRun.some((job) => job.isCancelled),
                // Aggregate cancelled dates from every job in the run, not just the
                // parent: expanding a run left re-parents it (ProjectService.BulkCreateJobs),
                // leaving the cancellations on the demoted (now child) job. Sourcing only
                // from rootJob would drop them and turn the whole run solid red.
                cancelledDates: jobRun.flatMap((job) => job.dateCancelled ?? []),
            });
        };

        forEach(groupedJobs, (sortedJobs, parentJobId) => {
            let currentRun = [sortedJobs[0]];

            const rootJob = project.jobs.find((job) => job.id == parentJobId) || project.jobs.find((job) => job.id == sortedJobs[0].id);

            for (let i = 1; i < sortedJobs.length; i += 1) {
                const previousDate = moment(sortedJobs[i - 1].jobDate).startOf('day');
                const currentDate = moment(sortedJobs[i].jobDate).startOf('day');

                if (currentDate.diff(previousDate, 'days') === 1) {
                    currentRun.push(sortedJobs[i]);
                } else {
                    createCalendarEvent(currentRun, rootJob);
                    currentRun = [sortedJobs[i]];
                }
            }

            createCalendarEvent(currentRun, rootJob);
        });

        setCalendarEvents(events);
    }, [project.contract?.number, project.jobs, project.id, getJobById]);

    const [viewStartDate, viewEndDate] = useMemo(() => [
        moment(currentDate).startOf(currentView).startOf('week').format(API_DATE_FORMAT),
        moment(currentDate).endOf(currentView).endOf('week').format(API_DATE_FORMAT),
    ], [currentDate, currentView]);

    // Handle calendar navigation
    const handleNavigation = useCallback((action) => {
        let targetMoment = moment();

        if (typeof action === 'string') {
            if (action === NAVIGATION_ACTIONS.PREVIOUS) {
                targetMoment = moment(viewStartDate, API_DATE_FORMAT).subtract(1, currentView);
            }
            if (action === NAVIGATION_ACTIONS.NEXT) {
                targetMoment = moment(viewEndDate, API_DATE_FORMAT).add(1, currentView);
            }
            if (action === NAVIGATION_ACTIONS.TODAY) {
                targetMoment = moment();
            }
        } else {
            targetMoment = moment(action);
        }

        setCurrentDate(targetMoment.toDate());
        setFocusedEvent(null);
    }, [currentView, viewEndDate, viewStartDate]);

    // Handle event drag and drop
    const handleEventDrop = useCallback(async ({ event, start }) => {
        // Store current events for potential rollback
        previousEventsRef.current = calendarEvents;

        const jobArray = Array.isArray(event.jobData) ? event.jobData : [event.jobData];
        const jobsHaveAssignments = jobArray.some((job) => job.hasAssignments);

        if (jobsHaveAssignments) {
            toasty.error('You cannot move a job that has confirmed assignments.');

            setCalendarEvents(calendarEvents);
            return;
        }

        const [primaryJob] = jobArray;

        if (typeof primaryJob.id === 'string' && !project.id) {
            onJobSelect({ action: 'move', event, start });
            return;
        }

        setJobsSaving(true);

        const durationDays = moment(event.end).diff(moment(event.start), 'days');
        const newStartDate = moment(start).startOf('day').toDate();
        const newEndDate = moment(newStartDate).add(durationDays, 'days').endOf('day').toDate();

        // Optimistic UI update
        setCalendarEvents(calendarEvents.map((evt) =>
            evt.sessionId === event.sessionId
                ? { ...evt, start: newStartDate, end: newEndDate }
                : evt,
        ));

        let confirmationText = 'Are you sure you want to move this job to a new date?';

        if (event.jobData.some((job) => job.hasRentalResources)) {
            confirmationText += ' Please make sure to adjust the rental start or end dates of any rental jobs moved.';
        }

        if (!window.confirm(confirmationText)) {
            setCalendarEvents(previousEventsRef.current);
            setJobsSaving(false);
            return;
        }

        try {
            // Calculate the time difference for all jobs in the group
            const jobDates = event.jobData.map((job) => new Date(job.jobDate)).sort((a, b) => a - b);
            const [earliestDate] = jobDates;
            const timeDifferenceMs = start.getTime() - earliestDate.getTime();
            const dayDifference = Math.round(timeDifferenceMs / (1000 * 60 * 60 * 24));

            // Prepare job updates
            const jobUpdates = event.jobData.map((job) => {
                const newJobDate = new Date(job.jobDate);
                newJobDate.setDate(newJobDate.getDate() + dayDifference);

                job.jobDate = newJobDate.toISOString();
                job.startTime = moment(newJobDate).format(DEFAULT_DATETIME_FORMAT);
                // `jobDate` is the job-site wall clock; keep it as the moved job's wall clock in
                // the job's own zone rather than stamping it with the viewer's offset.
                job.startTimeOffset = convertTimezone(newJobDate, resolveJobTimeZone(job, dispatchLocations));
                job.weekOfDate = moment(newJobDate).startOf('isoWeek').format('YYYY-MM-DD');

                return {
                    ...job,
                };
            });

            await httpClient.put(
                ApiRoutes.job.bulkMove(),
                jobUpdates,
                httpClient.format.json,
            );

            await refreshProject();

            toasty.success('Job(s) moved successfully');
        } catch (error) {
            console.error(error);
            toasty.error('Failed to move job');
            setCalendarEvents(previousEventsRef.current);
        } finally {
            setJobsSaving(false);
        }
    }, [calendarEvents, onJobSelect, project.id, setJobsSaving, refreshProject, dispatchLocations]);

    // Handle event resize
    const handleEventResize = useCallback(async ({ event, start, end }) => {
        setJobsSaving(true);
        previousEventsRef.current = calendarEvents;

        try {
            const startDay = moment(start).startOf('day');
            const endDay = moment(end).startOf('day');
            const dates = [];

            for (let d = startDay.clone(); d.isSameOrBefore(endDay, 'day'); d.add(1, 'day')) {
                dates.push(d.toDate());
            }

            setCalendarEvents((prev) => prev.map((evt) =>
                evt.sessionId === event.sessionId
                    ? { ...evt, start: startDay.toDate(), end: moment(end).endOf('day').toDate() }
                    : evt,
            ));

            const [firstJob] = event.jobData;
            const isDraftProject = !project.id;
            const isTempId = typeof firstJob.id === 'string';

            let sourceJob;

            if (isDraftProject || isTempId) {
                sourceJob = {
                    ...firstJob,
                    resources: (firstJob.resources || []).map((r) => ({
                        ...r,
                        rentalDropoffQuantity: r.rentalDropoffQuantity,
                        rentalPickupQuantity: r.rentalPickupQuantity,
                    })),
                };
            } else {
                const res = await httpClient.js(ApiRoutes.job.byId(firstJob.id));
                const apiJob = res.job ?? res;

                sourceJob = {
                    ...apiJob,
                    jobDate: apiJob.jobDate || apiJob.startTimeOffset || apiJob.jobDateOffset,
                    startTime: apiJob.startTimeOffset,
                    startTimeOffset: apiJob.startTimeOffset,
                    resources: (apiJob.resources ?? []).map((r) => ({
                        ...r,
                        rentalDropoffQuantity: r.rentalDropoffQuantity,
                        rentalPickupQuantity: r.rentalPickupQuantity,
                    })),
                };
            }

            const root = getJobById(firstJob.parentJobId || firstJob.id, true);
            const existing = [root, ...(root?.childJobs || [])];
            const cleanDates = dates.filter((d) =>
                !existing.find((j) => moment(j.jobDate).isSame(moment(d), 'day')),
            );

            if (!cleanDates.length) {
                setCalendarEvents(previousEventsRef.current);
                toasty.warning('No valid dates selected.');
                return;
            }

            setSpanDates(cleanDates);
            setSpanJob(sourceJob);
            setShowSpanModal(true);
        } catch (err) {
            console.error(err);
            toasty.error('Failed to prepare span.');
            setCalendarEvents(previousEventsRef.current);
        } finally {
            setJobsSaving(false);
        }
    }, [calendarEvents, getJobById, project.id, setJobsSaving]);

    const handleSelectJobFromList = useCallback((event, reactEvent = null, selectedDate = null) => {
        Alert.hide();

        if (reactEvent) {
            const node = reactEvent.currentTarget;
            const eventNodeList = node.querySelectorAll('.custom-event');

            if (eventNodeList && eventNodeList.length > 0) {
                const [eventNode] = eventNodeList;
                const slotStart = moment(eventNode.getAttribute('slotStart'));
                const slotEnd = moment(eventNode.getAttribute('slotEnd'));

                const jobDays = event.jobData.filter((job) => moment(job.jobDate) >= slotStart && moment(job.jobDate) < slotEnd).map((job) => job.jobDate);

                const spanDays = Math.round(
                    moment(jobDays[jobDays.length - 1]).startOf('day')
                        .diff(moment(jobDays[0]).startOf('day'), 'days'),
                ) + 1;

                const pixelsPerDay = node.getBoundingClientRect().width / spanDays;
                const clickOffsetX = reactEvent.clientX - node.getBoundingClientRect().left;
                const dayOffset = Math.floor(clickOffsetX / pixelsPerDay);
                const clickedDate = moment(jobDays[0]).add(dayOffset, 'days');

                const matchingJob = event.jobData.find((job) =>
                    moment(job.jobDate).isSame(clickedDate, 'day'),
                );

                if (matchingJob) {
                    const singleDayEvent = {
                        ...event,
                        start: clickedDate.toDate(),
                        end: clickedDate.toDate(),
                        jobData: [matchingJob],
                    };
                    onJobSelect({ action: 'select', event: singleDayEvent });
                } else {
                    onJobSelect({ action: 'select', event });
                }
            }
        } else {
            const matchingJob = event.jobData.find((job) => moment(job.jobDate).isSame(selectedDate, 'day'));

            if (matchingJob) {
                const singleDayEvent = {
                    ...event,
                    start: moment(matchingJob.jobDate).toDate(),
                    jobData: [matchingJob],
                };
                onJobSelect({ action: 'select', event: singleDayEvent });
            } else {
                onJobSelect({ action: 'select', event });
            }
        }
    }, [onJobSelect]);

    const handleToggleJobSelectList = useCallback((_, date) => {
        const dayEvents = orderBy(calendarEvents.filter((event) =>
            moment(date).isBetween(event.start, event.end, 'day', '[]'),
        ), (item) => item.start);

        Alert.show({
            title: `Jobs for ${moment(date).format('dddd, MMMM D, YYYY')}`,
            body: (
                <ListGroup>
                    {dayEvents.map((event) => (
                        <ListGroupItem
                            key={event.sessionId}
                        >
                            <FlexBetweenRow>
                                <ListGroupItemHeading className="mb-0 d-flex align-items-center">
                                    <Badge
                                        color="success"
                                        className="mr-2"
                                    >
                                        {getJobTime(event.jobData.find((job) => moment(job.jobDate).isSame(date, 'day')))}
                                    </Badge>

                                    {event.jobData.find((job) => moment(job.jobDate).isSame(date, 'day'))?.jobNumber ?? event.title}
                                </ListGroupItemHeading>

                                <div>
                                    <IconButton
                                        id={`focus-job-${event.sessionId}`}
                                        title="Focus Job"
                                        className="mr-2"
                                        onClick={() => {
                                            setFocusedEvent(event);
                                            Alert.hide();
                                        }}
                                    >
                                        <FontAwesomeIcon icon={faMagnifyingGlass} />
                                    </IconButton>

                                    <UncontrolledTooltip target={`focus-job-${event.sessionId}`}>
                                        Focus on this Job
                                    </UncontrolledTooltip>

                                    <SmallPrimaryButton
                                        onClick={() => handleSelectJobFromList(event, null, date)}
                                    >
                                        Open Job
                                    </SmallPrimaryButton>
                                </div>
                            </FlexBetweenRow>
                        </ListGroupItem>
                      ),
                    )}
                </ListGroup>
            ),
        });
    }, [calendarEvents, handleSelectJobFromList]);

    const cancelSpanApply = useCallback(() => {
        setShowSpanModal(false);
        setSpanDates([]);
        setSpanJob(null);
        setCalendarEvents(previousEventsRef.current);
    }, []);

    const confirmSpanApply = useCallback(() => {
        if (isApplying) return;
        setIsApplying(true);

        try {
            if (!spanJob || !spanDates.length) {
                cancelSpanApply();
                return;
            }

            // Prevent adding jobs to an invoiced period
            const invoicedDate = project.lastInvoiced
                ? moment(project.lastInvoiced)
                : null;

            if (invoicedDate) {
                const datesInInvoicedPeriod = spanDates.filter(
                    (date) => invoicedDate >= moment(date),
                );

                if (datesInInvoicedPeriod.length > 0) {
                    toasty.error('This date has already been invoiced. Please contact the billing team for changes.');
                    setIsApplying(false);
                    return;
                }
            }

            const newPayloads = spanDates.map((date) => {
                const dto = createJobCopyDto(spanJob, date, dispatchLocations);
                const normalizedResources = updateResources(spanJob.resources, date).map((r) => ({
                    ...r,
                    id: null,
                    jobId: 0,
                }));

                return {
                    ...dto,
                    id: null,
                    jobLocation: dto.jobLocation
                        ? { ...dto.jobLocation, id: dto.jobLocation.id ?? dto.jobLocationId ?? null }
                        : null,
                    meetingLocation: dto.meetingLocation
                        ? { ...dto.meetingLocation, id: dto.meetingLocation.id ?? dto.meetingLocationId ?? null }
                        : null,
                    resources: normalizedResources,
                };
            });

            onJobSelect({
                action: 'span-apply',
                payload: {
                    sourceJobId: spanJob.parentJobId || spanJob.id,
                    dates: spanDates,
                    jobs: newPayloads,
                },
            });

            if (!project.id) {
                toasty.success(`${pluralize(newPayloads.length, 'Job')} created successfully`);
            }
        } catch (e) {
            console.error(e);
            toasty.error('Failed to create jobs.');
            setCalendarEvents(previousEventsRef.current);
        } finally {
            setIsApplying(false);
            cancelSpanApply();
        }
    }, [cancelSpanApply, isApplying, onJobSelect, project.id, project.lastInvoiced, spanDates, spanJob, dispatchLocations]);

    return (
        <div className={cls('jobSchedulerCalendar', 'calendar-content', { 'is-recoloring': isSaving })}>
            <DragAndDropCalendar
                selectable
                toolbar
                views={CALENDAR_VIEWS}
                view={currentView}
                date={currentDate}
                defaultView="month"
                localizer={localizer}
                startAccessor="start"
                endAccessor="end"
                events={visibleCalendarEvents}
                longPressThreshold={75}
                style={{ minHeight: '675px' }}
                onSelectSlot={({ start }) => onDateSelect(start)}
                onSelectEvent={(event, reactEvent) => handleSelectJobFromList(event, reactEvent)}
                components={{
                    toolbar: JobSchedulerToolbar,
                    dateCellWrapper: ColoredDateCellWrapper,
                    event: (props) => (
                        <CustomEvent
                            {...props}
                            isFocusedEvent={!!focusedEvent}
                            setFocusedEvent={setFocusedEvent}
                            view={currentView}
                        />
                    ),
                    showMore: (props) => (
                        <CustomShowMore
                            {...props}
                            onShowMore={handleToggleJobSelectList}
                        />
                    ),
                }}
                onView={setCurrentView}
                onNavigate={handleNavigation}
                onShowMore={(events, date) => {
                    handleToggleJobSelectList(events, date);
                }}
                onEventDrop={handleEventDrop}
                onEventResize={handleEventResize}
                draggableAccessor={() => true}
                resizableAccessor={() => true}
                eventPropGetter={getEventStyles}
                key={calendarKey}
                popup={false}
            />

            <ModalForm
                isOpen={showSpanModal}
                onSubmit={() => confirmSpanApply()}
                toggle={() => cancelSpanApply()}
                title="Carry resources to new dates"
                size="lg"
                SaveButtonProps={{
                    label: 'Apply to new dates',
                    saving: isApplying,
                }}
            >
                <p className="mb-2">Review (and optionally update) the resources to carry over.</p>

                <div className="mb-3">
                    <strong>New dates:</strong>
                    <ul className="mt-2">
                        {spanDates.map((d, i) => (
                            <li key={i}>{moment(d).format('ddd, MMM D, YYYY')}</li>
                        ))}
                    </ul>
                </div>

                {spanJob && (
                    <ResourcePanel
                        job={spanJob}
                        contract={spanJob.contract || project.contract}
                        project={project}
                        selectedDate={spanJob.jobDate ? new Date(spanJob.jobDate) : new Date()}
                        edit={{ allowResources: true, dayStatuses: {}, resourceErrors: {} }}
                        onJobChange={(nextJob) => setSpanJob(nextJob)}
                        spanning
                        errors={{}}
                    />
                )}

                <p className="mt-3 mb-0 text-muted">
                    Rentals will default their dates to each new job’s date. “Apply” will create jobs for every date listed.
                </p>
            </ModalForm>
        </div>
    );
};
